import { Icon } from "@plone/volto/components";
import paintSVG from "@plone/volto/icons/paint.svg";
import heroSVG from "@plone/volto/icons/hero.svg";
import cardsSVG from "@plone/volto/icons/cards.svg";
import iconSVG from "@plone/volto/icons/select-all.svg";
import sliderSVG from "@plone/volto/icons/slider.svg";
import socialSVG from "@plone/volto/icons/share.svg";
import {
  Edit as CustomHeroEdit,
  View as CustomHeroView,
} from "./components/Blocks/CustomHero";
import {
  Edit as ButtonRowEdit,
  View as ButtonRowView,
} from "./components/Blocks/ButtonRow";
import {
  Edit as MultiCardEdit,
  View as MultiCardView,
} from "./components/Blocks/MultiCardBlock";
import {
  Edit as IconLinkRowEdit,
  View as IconLinkRowView,
} from "./components/Blocks/IconLinkRow";
import {
  Edit as EmblaCarouselEdit,
  View as EmblaCarouselView,
} from "./components/Blocks/EmblaCarousel";
import {
  Edit as SocialMetaEdit,
  View as SocialMetaView,
} from "./components/Blocks/SocialMeta";
import SocialSharing from "@codesyntax/volto-social-sharing/SocialSharing";
import { DEFAULT_SOCIAL } from "@codesyntax/volto-social-sharing/defaultSettings";
import { faSquareXTwitter } from "@fortawesome/free-brands-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import LeftColumnFacets from "./customizations/volto/components/manage/Blocks/Search/layout/LeftColumnFacets";
import RightColumnFacets from "./customizations/volto/components/manage/Blocks/Search/layout/RightColumnFacets";
import TopSideFacets from "./customizations/volto/components/manage/Blocks/Search/layout/TopSideFacets";
export {
  MASTER_COLOR_LIST,
  getColorChoices,
  isColorDark,
} from "./config/colors";

library.add(faSquareXTwitter);

// Wrapper to filter srcset and prevent small images from being selected
const CustomImage = (props) => {
  const { item, imageField = "image", ...rest } = props;

  // If we have image_scales, filter out scales that are too small
  if (item?.image_scales?.[imageField]) {
    const scales = item.image_scales[imageField];

    // Create a filtered version that excludes very small scales
    const filteredScales = scales.map((scale) => {
      if (scale.scales) {
        // Filter out scales smaller than 128px width to prevent pixelation
        const filteredScaleEntries = Object.entries(scale.scales).filter(
          ([key, scaleData]) => scaleData.width >= 128,
        );

        return {
          ...scale,
          scales: Object.fromEntries(filteredScaleEntries),
        };
      }
      return scale;
    });

    const modifiedItem = {
      ...item,
      image_scales: {
        ...item.image_scales,
        [imageField]: filteredScales,
      },
    };

    return (
      <ImageComponent {...rest} item={modifiedItem} imageField={imageField} />
    );
  }

  return <ImageComponent {...props} />;
};

const applyConfig = (config) => {
  config.settings.isMultilingual = false;
  config.settings.supportedLanguages = ["en"];
  config.settings.defaultLanguage = "en";
  config.blocks.themes = [
    {
      style: {
        "--theme-color": "white",
        "--theme-high-contrast-color": "var(--fsda-deep-space)",
        "--theme-foreground-color": "var(--fsda-deep-space)",
        "--theme-low-contrast-foreground-color": "var(--fsda-starlight)",
      },
      name: "default",
      label: "Default",
    },
    {
      style: {
        "--theme-color": "var(--fsda-starlight)",
        "--theme-high-contrast-color": "var(--fsda-deep-space)",
        "--theme-foreground-color": "var(--fsda-deep-space)",
        "--theme-low-contrast-foreground-color": "var(--lunar-regolith)",
      },
      name: "starlight",
      label: "Starlight",
    },
    {
      style: {
        "--theme-color": "var(--lunar-regolith)",
        "--theme-high-contrast-color": "var(--fsda-deep-space)",
        "--theme-foreground-color": "var(--fsda-deep-space)",
        "--theme-low-contrast-foreground-color": "var(--fsda-starlight)",
      },
      name: "lunar",
      label: "Lunar Regolith",
    },
  ];

  //Adding custom colours to Slate editor
  config.settings.slate.styleMenu.inlineStyles = [
    ...(config.settings.slate.styleMenu?.inlineStyles || []),
    {
      cssClass: "text-red",
      label: "Red Text",
      icon: () => <Icon name={paintSVG} size="18px" color="red" />,
    },
    {
      cssClass: "text-green",
      label: "Green Text",
      icon: () => <Icon name={paintSVG} size="18px" color="green" />,
    },
    {
      cssClass: "text-blue",
      label: "Blue Text",
      icon: () => <Icon name={paintSVG} size="18px" color="blue" />,
    },
  ];

  config.settings.slate.styleMenu.blockStyles = [
    ...(config.settings.slate.styleMenu?.blockStyles || []),
    {
      cssClass: "highlight-block",
      label: "Highlighted Block",
      nodeType: "paragraph", // Ensures it only applies to paragraph blocks
      icon: () => (
        <div
          style={{
            backgroundColor: "yellow",
            width: "18px",
            height: "18px",
            borderRadius: "3px",
          }}
        />
      ),
    },
    {
      cssClass: "center-text",
      label: "Center Text",
      icon: () => <Icon name={paintSVG} size="18px" />,
    },
  ];

  //CustomHero
  config.blocks.blocksConfig.customHero = {
    id: "customHero",
    title: "Hero Block",
    icon: heroSVG,
    group: "common",
    view: CustomHeroView,
    edit: CustomHeroEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //ButtonRow
  config.blocks.blocksConfig.buttonRow = {
    id: "buttonRow",
    title: "Button Row",
    icon: cardsSVG,
    group: "common",
    view: ButtonRowView,
    edit: ButtonRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //IconLinkRow
  config.blocks.blocksConfig.iconLinkRow = {
    id: "iconLinkRow",
    title: "Icon Link Row",
    icon: iconSVG,
    group: "common",
    view: IconLinkRowView,
    edit: IconLinkRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Multicard Block
  config.blocks.blocksConfig.multiCard = {
    id: "multiCard",
    title: "Multi Card",
    icon: cardsSVG,
    group: "common",
    view: MultiCardView,
    edit: MultiCardEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Embla Carousel
  config.blocks.blocksConfig.EmblaCarousel = {
    id: "EmblaCarousel",
    title: "Embla Carousel",
    icon: sliderSVG,
    group: "common",
    view: EmblaCarouselView,
    edit: EmblaCarouselEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Social Meta Block
  config.blocks.blocksConfig.socialMeta = {
    id: "socialMeta",
    title: "Social Media Meta",
    icon: socialSVG,
    group: "common",
    view: SocialMetaView,
    edit: SocialMetaEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Remove the Volto Light Theme's schema enhancer
  delete config.blocks.blocksConfig.search.schemaEnhancer;

  // Restore the original search block variations
  config.blocks.blocksConfig.search.variations = [
    {
      id: "facetsTopSide",
      title: "Facets on top",
      view: TopSideFacets,
      isDefault: true,
    },
    {
      id: "facetsLeftSide",
      title: "Facets on left",
      view: LeftColumnFacets,
    },
    {
      id: "facetsRightSide",
      title: "Facets on right",
      view: RightColumnFacets,
    },
  ];

  //Volto Social Sharing
  DEFAULT_SOCIAL.push({
    name: "LinkedIn",
    fa_name: ["fab", "linkedin-in"],
    color: "#0a66c2",
    sharing_url: "https://www.linkedin.com/sharing/share-offsite/?url=",
    id: "li",
  });
  let twitter = DEFAULT_SOCIAL.find((social) => social.id === "tw");
  if (twitter) {
    twitter.fa_name = faSquareXTwitter;
    twitter.color = "black";
    twitter.sharing_url =
      "https://x.com/intent/post?text=Check%20this%20out:&url=";
  }
  config.settings.appExtras = [
    ...config.settings.appExtras,
    {
      match: {
        path: ["**/news/**"],
        exact: false,
      },
      component: SocialSharing,
      props: {
        socialElements: DEFAULT_SOCIAL,
      },
    },
  ];

  return config;
};

export default applyConfig;
